import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from 'src/app/shared/shared.module';
import { TranslocoService } from '@jsverse/transloco';
import { ToastService } from 'src/app/shared/services/toast.service';
import { ContactModel } from 'src/app/modules/contact/models/contact.model';
import { FoundReportPdfRepository, SendFoundReportPayload } from 'src/app/modules/animals/states/pdf.repository';
import { AnimalMediaModel } from 'src/app/modules/animals/models/animal-media.model';
import { AnimalModel } from 'src/app/modules/animals/models/animal.model';
import { AnimalImagesRepository } from 'src/app/modules/animals/states/animal-images.repository';
import { AnimalOriginRepository } from 'src/app/modules/animals/states/animal-origin.repository';
import { Observable, of, switchMap, take } from 'rxjs';

@Component({
  selector: 'app-send-found-report-dialog',
  templateUrl: './send-found-report-dialog.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendFoundReportDialogComponent implements OnChanges {
  /** ID der AnimalWhereAbout */
  @Input() id: number | null = null;
  @Input() animal: AnimalModel | null = null;

  /** Contacts aus der gewählten zuständigen Behörde */
  @Input() availableContacts: ContactModel[] = [];

  /** Two-way binding für Sichtbarkeit */
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Wird emitted wenn das Senden erfolgreich war; übergibt die gespeicherte authorityInformation */
  @Output() sent = new EventEmitter<string | null>();

  /** Optionale Send-Funktion – wenn gesetzt, wird diese statt dem Standard-Endpoint verwendet */
  @Input() sendFn: ((id: number, payload: SendFoundReportPayload) => Observable<any>) | null = null;

  /** true → Sicherstellungsprotokoll (secured_by_police) statt Fundbericht versenden */
  @Input() securingReport: boolean = false;

  /** Behördeninformationen die in der E-Mail mitgeschickt werden */
  @Input() authorityInformation: string | null = null;

  /** Ausgewählte Contact-IDs (recipientContactIds) */
  selectedContactIds: number[] = [];

  editedAuthorityInformation: string | null = null;

  /** Freie E-Mail-Adressen (recipientCustoms) */
  customEmails: string[] = [''];

  isSending: boolean = false;

  mediaIds: number[] = [];
  showExistingMedia: boolean = false;
  animalMediaExists: boolean = false;
  animalMedia: AnimalMediaModel[] = [];
  selectedExistingMediaIds = new Set<number>();

  constructor(
    private readonly _foundReportPdfRepo: FoundReportPdfRepository,
    private readonly _toast: ToastService,
    private readonly _transloco: TranslocoService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _animalImageRepo: AnimalImagesRepository,
    private readonly _animalOriginRepo: AnimalOriginRepository
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.reset();

      // wenn Dialog aufgeht: prüfen ob Animal Medien hat
      const animalId = this.animal?.id;
      if (animalId) {
        this.loadExistingMedia(animalId);
      } else {
        this.animalMedia = [];
        this.animalMediaExists = false;
      }
    }
  }

  private loadExistingMedia(animalId: number): void {
    // Falls du Bilder+Videos getrennt hast, musst du hier ggf. beides laden/mergen.
    this._animalImageRepo.getImages(animalId).pipe(take(1)).subscribe(media => {
      this.animalMedia = media ?? [];
      this.animalMediaExists = this.animalMedia.length > 0;
      this._cdr.markForCheck();
    });
  }

  onMediaUploaded(ids: number[]) {
    const merged = new Set<number>(this.mediaIds);
    for (const id of ids) merged.add(id);
    this.mediaIds = Array.from(merged);

    // Mediathek neu laden damit Vorschau erscheint
    const animalId = this.animal?.id;
    if (animalId) {
      this._animalImageRepo.getImages(animalId).pipe(take(1)).subscribe(media => {
        this.animalMedia = media ?? [];
        this.animalMediaExists = this.animalMedia.length > 0;
        this._cdr.markForCheck();
      });
    } else {
      this._cdr.markForCheck();
    }
  }

  openExistingMediaModal(): void {
    // Startzustand: nichts vorausgewählt (weil "cancel alles verwerfen")
    this.selectedExistingMediaIds.clear();
    this.showExistingMedia = true;
    this._cdr.markForCheck();
  }

  closeExistingMediaModal(): void {
    this.showExistingMedia = false;
    this.selectedExistingMediaIds.clear();
    this._cdr.markForCheck();
  }

  toggleExistingMedia(mediaId: number): void {
    if (this.selectedExistingMediaIds.has(mediaId)) {
      this.selectedExistingMediaIds.delete(mediaId);
    } else {
      this.selectedExistingMediaIds.add(mediaId);
    }
    this._cdr.markForCheck();
  }

  isExistingMediaSelected(mediaId: number): boolean {
    return this.selectedExistingMediaIds.has(mediaId);
  }

  addSelectedExistingMedia(): void {
    const merged = new Set<number>(this.mediaIds);
    for (const id of this.selectedExistingMediaIds) merged.add(id);
    this.mediaIds = Array.from(merged);

    this.closeExistingMediaModal();
  }

  private reset(): void {
    this.selectedContactIds = [];
    this.customEmails = [''];
    this.isSending = false;
    this.mediaIds = [];
    this.selectedExistingMediaIds.clear();
    this.showExistingMedia = false;
    this.editedAuthorityInformation = this.authorityInformation ?? null;
  }


  /** Nur Contacts mit E-Mail anzeigen */
  get contactsWithEmail(): ContactModel[] {
    return this.availableContacts.filter(c => !!c.email?.trim());
  }

  get selectedMediaModels(): AnimalMediaModel[] {
    return this.animalMedia.filter(m => this.mediaIds.includes(m.id));
  }

  removeMedia(id: number): void {
    this.mediaIds = this.mediaIds.filter(mid => mid !== id);
    this._cdr.markForCheck();
  }

  get canSend(): boolean {
    const hasSelectedContact = this.selectedContactIds.length > 0;
    const hasValidCustomEmail = this.customEmails.some(e => this.isValidEmail(e));
    return (hasSelectedContact || hasValidCustomEmail) && !this.isSending && !!this.id;
  }

  isContactSelected(contactId: number): boolean {
    return this.selectedContactIds.includes(contactId);
  }

  toggleContact(contactId: number): void {
    if (this.isContactSelected(contactId)) {
      this.selectedContactIds = this.selectedContactIds.filter(id => id !== contactId);
    } else {
      this.selectedContactIds = [...this.selectedContactIds, contactId];
    }
  }

  addCustomEmail(): void {
    this.customEmails = [...this.customEmails, ''];
  }

  removeCustomEmail(index: number): void {
    // Wenn nur ein Feld existiert → einfach leeren
    if (this.customEmails.length === 1) {
      this.customEmails = [''];
      return;
    }

    if (index === 0) {
      // Erstes Feld nur leeren
      const updated = [...this.customEmails];
      updated[0] = '';
      this.customEmails = updated;
    } else {
      // Alle anderen Felder komplett entfernen
      this.customEmails = this.customEmails.filter((_, i) => i !== index);
    }
  }

  updateCustomEmail(index: number, value: string): void {
    const updated = [...this.customEmails];
    updated[index] = value;
    this.customEmails = updated;
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  getContactDisplayName(contact: ContactModel): string {
    const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.companyName || '';
    return name ? `${name} (${contact.email})` : contact.email ?? '';
  }

  send(): void {
    if (!this.canSend || !this.id) return;

    const payload: SendFoundReportPayload = {
      recipientContactIds: this.selectedContactIds,
      recipientCustoms: this.customEmails
        .map(e => e.trim())
        .filter(e => this.isValidEmail(e)),
      mediaIds: this.mediaIds,
    };

    this.isSending = true;
    this._cdr.markForCheck();

    const auth = this.editedAuthorityInformation || null;
    const id = this.id;

    // Origin-Versand: authorityInformation vorher auf das Origin speichern (Backend liest es von dort).
    // Bei custom sendFn (z. B. Pickup-Report → Whereabout) KEIN Origin-PUT.
    const persist$: Observable<unknown> = !this.sendFn
      ? this._animalOriginRepo.update(id, { authorityInformation: auth }, this.animal?.id ?? 0)
      : of(null);

    persist$
      .pipe(
        switchMap(() => {
          if (this.sendFn) return this.sendFn(id, payload);
          return this.securingReport
            ? this._foundReportPdfRepo.sendSecuringReport(id, payload)
            : this._foundReportPdfRepo.send(id, payload);
        }),
        take(1)
      )
      .subscribe((result: any) => {
        this.isSending = false;

        if (result !== null) {
          // Origin neu laden, damit das "PDF versendet"-Badge ohne Reload erscheint (nur Origin-Versand)
          if (!this.sendFn && this.animal?.id) {
            this._animalOriginRepo.getByAnimalId(this.animal.id, /*cached*/ false).pipe(take(1)).subscribe();
          }
          this._toast.showSuccess(
            this._transloco.translate('foundReport.send.toast.success')
          );
          this.sent.emit(auth);
          this.close();
        }

        this._cdr.markForCheck();
      });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

}
